import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PosCustomerPicker } from '@/components/pos/PosCustomerPicker.js';
import { listFeePresets } from '@/lib/api/fee-presets.js';
import {
  discountPresetsApi,
  type DiscountPreset,
} from '@/lib/api/discount-presets.js';
import type { StoreSettings } from '@/lib/api/stores.js';
import type { PosCartItem } from '@/lib/pos/types.js';
import {
  cartLineLabel,
  cartLineSubtitle,
  computeCartTotals,
  itemDiscountValue,
  itemLineTotal,
  roundMoney,
} from '@/lib/pos/cart-math.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { createFeeCartItem, usePosCartStore } from '@/stores/pos-cart.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import {
  discountAmountToPct,
  evaluateDiscount,
  isManagerRole,
  roleMeetsMinimum,
} from '@/lib/permissions.js';
import { useAuthStore } from '@/stores/auth.js';

type PosCartPanelProps = {
  store: StoreSettings;
};

function presetMatchesItem(preset: DiscountPreset, item: PosCartItem): boolean {
  if (preset.appliesTo === 'all') return true;
  if (preset.appliesTo === 'repairs') return item.type === 'repair';
  if (preset.appliesTo === 'parts' || preset.appliesTo === 'accessories') {
    return item.type === 'inventory';
  }
  return true;
}

function presetLabel(preset: DiscountPreset): string {
  const value =
    preset.discountType === 'percent' ? `${Number(preset.value)}%` : formatCurrency(preset.value);
  return `${preset.name} ${value}`;
}

export function PosCartPanel({ store }: PosCartPanelProps) {
  const items = usePosCartStore((s) => s.items);
  const customer = usePosCartStore((s) => s.customer);
  const cartDiscount = usePosCartStore((s) => s.cartDiscount);
  const loyaltyApplied = usePosCartStore((s) => s.loyaltyApplied);
  const storeCreditApplied = usePosCartStore((s) => s.storeCreditApplied);
  const updateItem = usePosCartStore((s) => s.updateItem);
  const removeItem = usePosCartStore((s) => s.removeItem);
  const setCustomer = usePosCartStore((s) => s.setCustomer);
  const setCartDiscount = usePosCartStore((s) => s.setCartDiscount);
  const setLoyaltyApplied = usePosCartStore((s) => s.setLoyaltyApplied);
  const setStoreCreditApplied = usePosCartStore((s) => s.setStoreCreditApplied);
  const setManagerOverrideAuthorizedBy = usePosCartStore((s) => s.setManagerOverrideAuthorizedBy);
  const addItem = usePosCartStore((s) => s.addItem);

  const userRole = useAuthStore((s) => s.user?.role ?? 'read_only');
  const { flags } = usePermissions();
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();

  const [discountItemId, setDiscountItemId] = useState<string | null>(null);
  const [itemDiscountType, setItemDiscountType] = useState<'flat' | 'percent'>('flat');
  const [itemDiscountAmount, setItemDiscountAmount] = useState('');
  const [itemDiscountReason, setItemDiscountReason] = useState('');
  const [itemPresetSearch, setItemPresetSearch] = useState('');
  const [showCartDiscountForm, setShowCartDiscountForm] = useState(false);
  const [cartDiscountType, setCartDiscountType] = useState<'flat' | 'percent'>('flat');
  const [cartDiscountAmount, setCartDiscountAmount] = useState('');
  const [cartDiscountReason, setCartDiscountReason] = useState('');
  const [cartPresetSearch, setCartPresetSearch] = useState('');
  const [pendingItemPresetId, setPendingItemPresetId] = useState<string | null>(null);
  const [pendingCartPresetId, setPendingCartPresetId] = useState<string | null>(null);

  const taxRate = Number(store.taxRate ?? 0);
  const taxMode = store.taxMode ?? 'parts_only';
  const totals = computeCartTotals(items, cartDiscount, taxRate, taxMode);
  const reasonRequired = flags.discount_require_reason ?? true;
  const priceOverridesEnabled = flags.price_override_allowed !== false;
  const [priceEditBaseline, setPriceEditBaseline] = useState<Record<string, number>>({});

  const feePresetsQuery = useQuery({
    queryKey: ['pos-fee-presets'],
    queryFn: () => listFeePresets(),
  });

  const discountPresetsQuery = useQuery({
    queryKey: ['discount-presets', 'active', 'pos'],
    queryFn: () => discountPresetsApi.list(),
    staleTime: 60_000,
  });

  const allPresets = discountPresetsQuery.data ?? [];
  const cartPresets = useMemo(
    () => allPresets.filter((p) => p.appliesTo === 'all'),
    [allPresets],
  );
  const visibleCartPresets = useMemo(() => {
    const q = cartPresetSearch.trim().toLowerCase();
    const filtered = q
      ? cartPresets.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            (p.code ?? '').toLowerCase().includes(q),
        )
      : cartPresets;
    return filtered.slice(0, 8);
  }, [cartPresets, cartPresetSearch]);

  async function commitUnitPrice(itemId: string) {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    const next = roundMoney(Math.max(item.unitPrice, 0));
    const baseline = roundMoney(priceEditBaseline[itemId] ?? item.unitPrice);
    const catalogPrice = roundMoney(Number(item.originalPrice ?? baseline));

    if (next === baseline) {
      updateItem(itemId, { unitPrice: next });
      return;
    }

    if (next !== catalogPrice) {
      if (!priceOverridesEnabled) {
        toastError('Price overrides are disabled for this store');
        updateItem(itemId, { unitPrice: baseline });
        return;
      }
      const minRole = flags.price_override_min_role ?? 'front_desk';
      const belowMinRole = !roleMeetsMinimum(userRole, minRole);
      const requireMgrOverride =
        (flags.price_override_require_pin ?? true) && !isManagerRole(userRole);
      if (belowMinRole || requireMgrOverride) {
        const result = await requireOverride({
          action: 'price_override',
          title: 'Price Override Requires Approval',
          description: `Changing price from ${formatCurrency(baseline)} to ${formatCurrency(next)}`,
          context: {
            amount: next,
            reason: `POS price ${formatCurrency(baseline)} → ${formatCurrency(next)}`,
          },
        });
        if (!result.approved) {
          updateItem(itemId, { unitPrice: baseline });
          return;
        }
        setManagerOverrideAuthorizedBy(result.authorizedBy.id);
      }
    }

    updateItem(itemId, { unitPrice: next });
  }

  async function applyItemPreset(itemId: string, preset: DiscountPreset) {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    if (!presetMatchesItem(preset, item)) {
      toastError('This preset does not apply to this line');
      return;
    }
    if (preset.minRole && !roleMeetsMinimum(userRole, preset.minRole)) {
      toastError(`This discount requires ${prettyLabel(preset.minRole)} role`);
      return;
    }

    const note = itemDiscountReason.trim();
    if (preset.requiresNote && !note) {
      setPendingItemPresetId(preset.id);
      setItemDiscountType(preset.discountType === 'percent' ? 'percent' : 'flat');
      setItemDiscountAmount(String(Number(preset.value)));
      toastError('This discount requires a note');
      return;
    }

    const lineBase = roundMoney(item.unitPrice * item.quantity);
    const value = Number(preset.value);
    const isPercent = preset.discountType === 'percent';
    const discountMoney = isPercent
      ? roundMoney((lineBase * value) / 100)
      : Math.min(value, lineBase);
    const discountPct = isPercent ? value : discountAmountToPct(discountMoney, lineBase);

    if (preset.requiresApproval) {
      const result = await requireOverride({
        action: 'apply_discount',
        title: 'Discount Requires Approval',
        description: `${preset.name}: ${presetLabel(preset)} (${formatCurrency(discountMoney)})`,
        requiredRole: 'store_manager',
        context: {
          percent: discountPct,
          amount: discountMoney,
          originalPrice: lineBase,
          requestedPrice: Math.max(lineBase - discountMoney, 0),
          itemName: cartLineLabel(item),
          reason: note || preset.name,
        },
      });
      if (!result.approved) return;
      setManagerOverrideAuthorizedBy(result.authorizedBy.id);
    }

    updateItem(itemId, {
      discountAmount: isPercent ? 0 : value,
      discountPct: isPercent ? value : 0,
      discountReason: note || preset.name,
      discountPresetId: preset.id,
    });
    setDiscountItemId(null);
    setItemDiscountAmount('');
    setItemDiscountReason('');
    setItemPresetSearch('');
    setPendingItemPresetId(null);
    toastSuccess(`${preset.name} applied`);
  }

  async function applyItemDiscount(itemId: string) {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;

    if (pendingItemPresetId) {
      const preset = allPresets.find((p) => p.id === pendingItemPresetId);
      if (preset) {
        await applyItemPreset(itemId, preset);
        return;
      }
    }

    const amount = Math.max(Number(itemDiscountAmount || 0), 0);
    if (amount <= 0) {
      updateItem(itemId, {
        discountAmount: 0,
        discountPct: 0,
        discountReason: '',
        discountPresetId: null,
      });
      setDiscountItemId(null);
      setItemDiscountAmount('');
      setItemDiscountReason('');
      setPendingItemPresetId(null);
      return;
    }

    const lineBase = roundMoney(item.unitPrice * item.quantity);
    const discountPct =
      itemDiscountType === 'percent' ? amount : discountAmountToPct(amount, lineBase);
    const reason = itemDiscountReason.trim();
    const evaluation = evaluateDiscount({
      flags,
      userRole,
      discountPct,
      hasReason: Boolean(reason) || !reasonRequired,
    });
    if (!evaluation.allowed) {
      toastError(evaluation.reason ?? 'Discount not allowed');
      return;
    }
    if (evaluation.needsManagerOverride) {
      const discountAmount =
        itemDiscountType === 'flat' ? amount : roundMoney((lineBase * amount) / 100);
      const result = await requireOverride({
        action: 'apply_discount',
        title: 'Discount Requires Approval',
        description: `Applying ${discountPct.toFixed(0)}% discount (${formatCurrency(discountAmount)})`,
        requiredRole: 'store_manager',
        context: { percent: discountPct, amount: discountAmount, reason: reason || undefined },
      });
      if (!result.approved) return;
      setManagerOverrideAuthorizedBy(result.authorizedBy.id);
    }

    updateItem(itemId, {
      discountAmount: itemDiscountType === 'flat' ? amount : 0,
      discountPct: itemDiscountType === 'percent' ? amount : 0,
      discountReason:
        reason || (itemDiscountType === 'percent' ? `${amount}% off` : 'Item discount'),
      discountPresetId: null,
    });
    setDiscountItemId(null);
    setItemDiscountAmount('');
    setItemDiscountReason('');
    setPendingItemPresetId(null);
  }

  async function applyCartPreset(preset: DiscountPreset) {
    if (items.length === 0) {
      toastError('Add items before applying a discount');
      return;
    }
    if (preset.minRole && !roleMeetsMinimum(userRole, preset.minRole)) {
      toastError(`This discount requires ${prettyLabel(preset.minRole)} role`);
      return;
    }

    const note = cartDiscountReason.trim();
    if (preset.requiresNote && !note) {
      setPendingCartPresetId(preset.id);
      setShowCartDiscountForm(true);
      setCartDiscountType(preset.discountType === 'percent' ? 'percent' : 'flat');
      setCartDiscountAmount(String(Number(preset.value)));
      toastError('This discount requires a note');
      return;
    }

    const base = totals.discountedSubtotal;
    if (base <= 0) {
      toastError('Nothing to discount');
      return;
    }
    const value = Number(preset.value);
    const isPercent = preset.discountType === 'percent';
    const discountMoney = isPercent
      ? roundMoney((base * value) / 100)
      : Math.min(value, base);
    const discountPct = isPercent ? value : discountAmountToPct(discountMoney, base);

    if (preset.requiresApproval) {
      const result = await requireOverride({
        action: 'apply_discount',
        title: 'Discount Requires Approval',
        description: `${preset.name}: ${presetLabel(preset)} (${formatCurrency(discountMoney)})`,
        requiredRole: 'store_manager',
        context: {
          percent: discountPct,
          amount: discountMoney,
          originalPrice: base,
          requestedPrice: Math.max(base - discountMoney, 0),
          reason: note || preset.name,
        },
      });
      if (!result.approved) return;
      setManagerOverrideAuthorizedBy(result.authorizedBy.id);
    }

    setCartDiscount({
      type: isPercent ? 'percent' : 'flat',
      amount: value,
      reason: note || preset.name,
      discountPresetId: preset.id,
    });
    setShowCartDiscountForm(false);
    setCartDiscountAmount('');
    setCartDiscountReason('');
    setCartPresetSearch('');
    setPendingCartPresetId(null);
    toastSuccess(`${preset.name} applied to cart`);
  }

  async function applyCartDiscount() {
    if (pendingCartPresetId) {
      const preset = allPresets.find((p) => p.id === pendingCartPresetId);
      if (preset) {
        await applyCartPreset(preset);
        return;
      }
    }

    const amount = Math.max(Number(cartDiscountAmount || 0), 0);
    if (amount <= 0) {
      toastError('Enter a discount amount');
      return;
    }
    const reason = cartDiscountReason.trim();
    if (!reason && reasonRequired) {
      toastError('Discount reason is required');
      return;
    }
    const discountPct =
      cartDiscountType === 'percent'
        ? amount
        : discountAmountToPct(amount, totals.discountedSubtotal);
    const evaluation = evaluateDiscount({
      flags,
      userRole,
      discountPct,
      hasReason: Boolean(reason) || !reasonRequired,
    });
    if (!evaluation.allowed) {
      toastError(evaluation.reason ?? 'Discount not allowed');
      return;
    }
    if (evaluation.needsManagerOverride) {
      const discountAmount =
        cartDiscountType === 'flat'
          ? amount
          : roundMoney((totals.discountedSubtotal * amount) / 100);
      const result = await requireOverride({
        action: 'apply_discount',
        title: 'Discount Requires Approval',
        description: `Applying ${discountPct.toFixed(0)}% discount (${formatCurrency(discountAmount)})`,
        requiredRole: 'store_manager',
        context: { percent: discountPct, amount: discountAmount, reason: reason || undefined },
      });
      if (!result.approved) return;
      setManagerOverrideAuthorizedBy(result.authorizedBy.id);
    }

    setCartDiscount({
      type: cartDiscountType,
      amount,
      reason:
        cartDiscountType === 'percent'
          ? `${reason || 'Cart discount'} (${amount}% off)`
          : reason || 'Cart discount',
      discountPresetId: null,
    });
    setShowCartDiscountForm(false);
    setCartDiscountAmount('');
    setCartDiscountReason('');
    setPendingCartPresetId(null);
  }

  const loyaltyPts = Number(customer?.loyaltyPointsBalance ?? 0);
  const loyaltyValue = roundMoney(loyaltyPts / 100);
  const storeCredit = Number(customer?.storeCreditBalance ?? 0);

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden border-r border-slate-200 bg-white">
      <div className="shrink-0 border-b border-slate-200 p-3">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Customer</div>
        {customer ? (
          <div className="mt-1 flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-950">
              {customer.firstName} {customer.lastName ?? ''}
              {loyaltyPts > 0 ? (
                <span className="ml-2 text-xs font-normal text-green-700">
                  {loyaltyPts} pts ({formatCurrency(loyaltyValue)})
                </span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setCustomer(null)}
              className="text-xs font-semibold text-primary hover:underline"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="mt-1 space-y-2">
            <PosCustomerPicker variant="compact" />
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3">
        {items.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-400">
            Scan an item or select from the left panel
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const linePresets = allPresets.filter((p) => presetMatchesItem(p, item));
              const q = itemPresetSearch.trim().toLowerCase();
              const shownLinePresets = (q
                ? linePresets.filter(
                    (p) =>
                      p.name.toLowerCase().includes(q) ||
                      (p.code ?? '').toLowerCase().includes(q),
                  )
                : linePresets
              ).slice(0, 8);

              return (
                <div key={item.id} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-950">{cartLineLabel(item)}</div>
                      {cartLineSubtitle(item) ? (
                        <div className="text-xs text-slate-500">{cartLineSubtitle(item)}</div>
                      ) : null}
                    </div>
                    <button type="button" onClick={() => removeItem(item.id)} className="text-red-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-[auto_1fr_auto] items-center gap-2">
                    <div className="flex items-center rounded-lg border border-slate-200 text-sm">
                      <button
                        type="button"
                        className="px-2 py-1"
                        onClick={() =>
                          updateItem(item.id, { quantity: Math.max(1, item.quantity - 1) })
                        }
                      >
                        −
                      </button>
                      <span className="px-2">{item.quantity}</span>
                      <button
                        type="button"
                        className="px-2 py-1"
                        onClick={() => updateItem(item.id, { quantity: item.quantity + 1 })}
                      >
                        +
                      </button>
                    </div>
                    <div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-400">$</span>
                        <input
                          className="w-20 rounded border border-slate-300 px-2 py-0.5 text-sm font-semibold disabled:bg-slate-100"
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          disabled={!priceOverridesEnabled}
                          onFocus={() =>
                            setPriceEditBaseline((prev) => ({
                              ...prev,
                              [item.id]: roundMoney(item.unitPrice),
                            }))
                          }
                          onChange={(e) =>
                            updateItem(item.id, {
                              unitPrice: Math.max(Number(e.target.value || 0), 0),
                            })
                          }
                          onBlur={() => void commitUnitPrice(item.id)}
                        />
                      </div>
                      <button
                        type="button"
                        className="mt-0.5 text-[10px] font-bold text-primary"
                        onClick={() => {
                          setDiscountItemId(discountItemId === item.id ? null : item.id);
                          setItemDiscountType(item.discountPct ? 'percent' : 'flat');
                          setItemDiscountAmount(
                            String(item.discountPct || item.discountAmount || ''),
                          );
                          setItemDiscountReason(item.discountReason ?? '');
                          setPendingItemPresetId(item.discountPresetId ?? null);
                          setItemPresetSearch('');
                        }}
                      >
                        % disc
                      </button>
                    </div>
                    <div className="text-sm font-bold">{formatCurrency(itemLineTotal(item))}</div>
                  </div>
                  {discountItemId === item.id ? (
                    <div className="mt-2 space-y-2 rounded-lg bg-slate-50 p-2">
                      {linePresets.length > 0 ? (
                        <div>
                          <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                            Quick
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {shownLinePresets.map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => void applyItemPreset(item.id, p)}
                                className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-800 hover:border-primary hover:text-primary"
                              >
                                {presetLabel(p)}
                              </button>
                            ))}
                          </div>
                          {linePresets.length > 8 ? (
                            <input
                              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                              placeholder="Search presets…"
                              value={itemPresetSearch}
                              onChange={(e) => setItemPresetSearch(e.target.value)}
                            />
                          ) : null}
                        </div>
                      ) : null}
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        Custom
                      </p>
                      <div className="flex gap-2">
                        <select
                          className="rounded border border-slate-300 px-2 py-1 text-xs"
                          value={itemDiscountType}
                          onChange={(e) => {
                            setItemDiscountType(e.target.value as 'flat' | 'percent');
                            setPendingItemPresetId(null);
                          }}
                        >
                          <option value="flat">$ Flat</option>
                          <option value="percent">% Percent</option>
                        </select>
                        <input
                          className="w-20 rounded border border-slate-300 px-2 py-1 text-xs"
                          type="number"
                          value={itemDiscountAmount}
                          onChange={(e) => {
                            setItemDiscountAmount(e.target.value);
                            setPendingItemPresetId(null);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => void applyItemDiscount(item.id)}
                          className="rounded bg-primary px-2 py-1 text-xs font-bold text-white"
                        >
                          Apply
                        </button>
                      </div>
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        placeholder={
                          reasonRequired || pendingItemPresetId
                            ? 'Reason / note'
                            : 'Reason (optional)'
                        }
                        value={itemDiscountReason}
                        onChange={(e) => setItemDiscountReason(e.target.value)}
                      />
                    </div>
                  ) : null}
                  {itemDiscountValue(item) > 0 ? (
                    <div className="mt-1 text-xs text-green-700">
                      −{formatCurrency(itemDiscountValue(item))}
                      {item.discountReason ? (
                        <span className="text-slate-500"> · {item.discountReason}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-3 border-t border-slate-200 p-3">
        {(feePresetsQuery.data ?? []).length ? (
          <div className="flex gap-1 overflow-x-auto pb-1">
            {(feePresetsQuery.data ?? []).map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() =>
                  addItem(
                    createFeeCartItem({
                      feePresetId: preset.id,
                      name: preset.name,
                      price: Number(preset.amount),
                    }),
                  )
                }
                className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-700 hover:bg-slate-100"
              >
                {preset.name} +{formatCurrency(preset.amount)}
              </button>
            ))}
          </div>
        ) : null}

        {/* Cart-level discount presets */}
        {cartPresets.length > 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Discount presets
              </p>
              {cartDiscount ? (
                <button
                  type="button"
                  className="text-[10px] font-bold text-red-600"
                  onClick={() => setCartDiscount(null)}
                >
                  Clear
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-1">
              {visibleCartPresets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={items.length === 0}
                  onClick={() => void applyCartPreset(p)}
                  className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-800 hover:border-primary hover:text-primary disabled:opacity-40"
                >
                  {presetLabel(p)}
                </button>
              ))}
            </div>
            {cartPresets.length > 8 ? (
              <input
                className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                placeholder="Search more presets…"
                value={cartPresetSearch}
                onChange={(e) => setCartPresetSearch(e.target.value)}
              />
            ) : null}
          </div>
        ) : null}

        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Subtotal</span>
            <span>{formatCurrency(totals.subtotal)}</span>
          </div>
          {cartDiscount ? (
            <div className="flex justify-between text-green-700">
              <span className="truncate pr-2">
                Discount
                {cartDiscount.reason ? (
                  <span className="font-normal text-slate-500"> · {cartDiscount.reason}</span>
                ) : null}
              </span>
              <span className="shrink-0">−{formatCurrency(totals.cartDiscountValue)}</span>
            </div>
          ) : !showCartDiscountForm ? (
            <button
              type="button"
              className="text-xs font-bold text-primary"
              onClick={() => setShowCartDiscountForm(true)}
            >
              + Custom discount
            </button>
          ) : null}
          {showCartDiscountForm ? (
            <div className="space-y-2 rounded-lg bg-slate-50 p-2">
              <div className="flex gap-1">
                {(['flat', 'percent'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setCartDiscountType(t);
                      setPendingCartPresetId(null);
                    }}
                    className={[
                      'flex-1 rounded py-1 text-xs font-bold',
                      cartDiscountType === t ? 'bg-primary text-white' : 'bg-white text-slate-600',
                    ].join(' ')}
                  >
                    {t === 'flat' ? '$' : '%'}
                  </button>
                ))}
              </div>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                placeholder="Amount"
                value={cartDiscountAmount}
                onChange={(e) => {
                  setCartDiscountAmount(e.target.value);
                  setPendingCartPresetId(null);
                }}
              />
              <input
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                placeholder={
                  reasonRequired || pendingCartPresetId
                    ? 'Reason required'
                    : 'Reason (optional)'
                }
                value={cartDiscountReason}
                onChange={(e) => setCartDiscountReason(e.target.value)}
              />
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setShowCartDiscountForm(false);
                    setPendingCartPresetId(null);
                  }}
                  className="flex-1 rounded border border-slate-300 py-1 text-xs font-bold text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void applyCartDiscount()}
                  className="flex-1 rounded bg-primary py-1 text-xs font-bold text-white"
                >
                  Apply
                </button>
              </div>
            </div>
          ) : null}
          {totals.tax > 0 ? (
            <div className="flex justify-between">
              <span className="text-slate-600">
                {taxMode === 'parts_only' ? 'Parts Tax' : 'Tax'} ({(taxRate * 100).toFixed(1)}%)
              </span>
              <span>{formatCurrency(totals.tax)}</span>
            </div>
          ) : null}
          <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
            <span>Total</span>
            <span>{formatCurrency(totals.total - loyaltyApplied - storeCreditApplied)}</span>
          </div>
        </div>

        {customer && loyaltyPts > 0 ? (
          <div className="flex items-center justify-between rounded-lg bg-green-50 px-3 py-2 text-xs">
            <span>🎁 Apply {loyaltyPts} pts (−{formatCurrency(loyaltyValue)})</span>
            <button
              type="button"
              onClick={() => {
                setLoyaltyApplied(loyaltyApplied > 0 ? 0 : Math.min(loyaltyValue, totals.total));
                toastSuccess(loyaltyApplied > 0 ? 'Loyalty removed' : 'Loyalty applied');
              }}
              className="rounded bg-white px-2 py-0.5 font-bold text-green-800"
            >
              {loyaltyApplied > 0 ? 'Remove' : 'Apply'}
            </button>
          </div>
        ) : null}
        {customer && storeCredit > 0 ? (
          <div className="flex items-center justify-between rounded-lg bg-green-50 px-3 py-2 text-xs">
            <span>💳 Apply {formatCurrency(storeCredit)} store credit</span>
            <button
              type="button"
              onClick={() => {
                setStoreCreditApplied(
                  storeCreditApplied > 0 ? 0 : Math.min(storeCredit, totals.total),
                );
                toastSuccess(
                  storeCreditApplied > 0 ? 'Store credit removed' : 'Store credit applied',
                );
              }}
              className="rounded bg-white px-2 py-0.5 font-bold text-green-800"
            >
              {storeCreditApplied > 0 ? 'Remove' : 'Apply'}
            </button>
          </div>
        ) : null}
      </div>

      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </section>
  );
}
